import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { usePendingWorkUnits, type PendingWorkUnitRow } from "@/api/hooks/timetracking";
import { ProgressCell } from "@/shared/components/ProgressCell";
import { formatDate } from "@/shared/utils/format";

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (wu: PendingWorkUnitRow) => void;
  title?: string;
  excludeWorkUnitId?: string | null;
  /**
   * Forced mode: user MUST pick a work unit. Hides Cancel, disables backdrop /
   * Escape close. Used by SessionStartGate after mark-in.
   */
  forced?: boolean;
  subtitle?: string;
};

export function WorkUnitPickerDialog({
  open,
  onClose,
  onPick,
  title = "Pick a Work Unit",
  excludeWorkUnitId,
  forced = false,
  subtitle,
}: Props) {
  const [search, setSearch] = useState("");
  // Server filters by `search` (ctrl_no / project / client name). Client only
  // strips out the currently active session WU. Client-side search would
  // mask rows beyond the server's row-slice limit on users with many
  // assignments (e.g. legacy-migrated employees with 70+ pending WUs).
  const list = usePendingWorkUnits(open, search.trim());
  const filtered = (list.data ?? []).filter((r) =>
    excludeWorkUnitId ? r.workUnitId !== excludeWorkUnitId : true,
  );

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (forced && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
        onClose();
      }}
      disableEscapeKeyDown={forced}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: "wm-wu-picker-paper" }}
    >
      <DialogTitle>
        {title}
        {subtitle ? (
          <div style={{ fontSize: 12, fontWeight: 400, color: "var(--ink-3)", marginTop: 4 }}>
            {subtitle}
          </div>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by ctrl no., project, client…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
        {list.isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
            No work units available.
          </div>
        ) : (
          <div className="wm-wu-picker-list">
            {filtered.map((r) => {
              const pct = r.totalSteps > 0 ? (r.stepsCompleted / r.totalSteps) * 100 : 0;
              return (
                <button
                  key={r.workUnitId}
                  type="button"
                  onClick={() => onPick(r)}
                  className="wm-wu-picker-row"
                >
                  <span className="wm-wu-picker-ctrl mono">{r.ctrlNo}</span>
                  <span className="wm-wu-picker-main">
                    <span className="wm-wu-picker-title">{r.projectName ?? "—"}</span>
                    <span className="wm-wu-picker-meta">
                      {r.clientName ?? "—"} · {r.currentWorkflowName ?? "—"}
                    </span>
                  </span>
                  <span className="wm-wu-picker-prog">
                    <ProgressCell value={pct} label={`${r.stepsCompleted}/${r.totalSteps}`} />
                  </span>
                  <span className="wm-wu-picker-last">
                    {r.lastWorkedAt ? `Last: ${formatDate(r.lastWorkedAt)}` : "Not worked yet"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {!forced && <Button onClick={onClose}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}
